import React from "react";
import moment from "moment";
import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Label,
} from "recharts";
import { formatMass } from "@signolteam/shared/units-of-measure";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import { AppUtils } from "@src/utils";
import { useDCContext } from "@src/context/dcContext";
import { COMMON_CONTROLS } from "@src/stories/utils";
import CardContainer from "@src/components/cards/CardContainer";

// TODO - Create story and clean up card
const GoalHistoricalPerformanceCard = (props) => {
  const { target, behaviour, industry, myTargets, firstEnrolledDate } = props;

  const { getDynamicReplacement } = useDCContext();

  const industryFormat = industry === "shipping" ? "YYYY-MM-DD" : "YYYY-MM";
  const dateFormat = industry === "shipping" ? "DD MMM" : "MMM";
  const bvrColor = getBehaviourColourForPosition(behaviour.order);

  let fuelSaved = 0;
  let bestFuelSaved = 0;
  let bestPeriod = "";
  let goalNeverAchieved = true;
  let targetsByGroup = {};

  myTargets.forEach((targetData) => {
    if (
      targetData.behaviours?.length > 0 &&
      moment
        .utc(targetData.groupKey, industryFormat)
        .isSameOrAfter(moment.utc(firstEnrolledDate), "day")
    ) {
      const periodLabel =
        industry === "shipping"
          ? targetData.groupKey
          : `${targetData.groupKey}-01`;
      const foundData = targetData.behaviours.find(
        (t) =>
          t.behaviourAcronym === target.behaviourAcronym &&
          target.goalTargetEvents > 0,
      );
      if (foundData) {
        targetsByGroup[periodLabel] = {
          ...foundData,
          performanceRate: foundData.succeeded - foundData.goalTargetEvents,
        };

        const fuel = foundData.totalSavingsFuelKg;
        fuelSaved += fuel || 0;
        if (fuel > bestFuelSaved) {
          bestFuelSaved = fuel;
          bestPeriod = moment
            .utc(periodLabel, "YYYY/MM/DD")
            .format("MMMM, YYYY");
        }
        if (foundData.succeeded >= foundData.goalTargetEvents) {
          goalNeverAchieved = false;
        }
      }
    }
  });

  // Sort from past to future
  const months = Object.keys(targetsByGroup).sort((a, b) => (a > b ? 1 : -1));
  // remove current month
  months.pop();
  const tByGroup = months.map((key) => targetsByGroup[key].totalSavingsFuelKg);
  const displayAsTonnes = industry === "shipping" || fuelSaved / 1000 > 1;

  const getDataset = (graphData, monthLabels, displayAsTonnes) => {
    let values = {};
    let data = graphData.length > 6 ? graphData.slice(-6) : graphData;
    let labels = graphData.length > 6 ? monthLabels.slice(-6) : monthLabels;
    let flatData = [];

    data.forEach((value, i) => {
      const val = displayAsTonnes ? value / 1000 : value;
      values[labels[i]] = val;
      flatData.push({
        label: labels[i],
        date: moment.utc(labels[i], industryFormat),
        name: moment.utc(labels[i], industryFormat).format(dateFormat),
        y: val,
      });
    });
    return {
      borderSkipped: false,
      values: values,
      flatData: flatData,
      units: displayAsTonnes ? (industry === "shipping" ? "MT" : "t") : "kg",
    };
  };
  const data = getDataset(tByGroup, months, displayAsTonnes);

  const maxY = Math.max(...data.flatData.map((d) => d.y));

  return (
    <CardContainer px={2} py={2} shadow={false}>
      <div className="mx-auto text-left sm:text-left w-full p-4">
        <div className="flex flex-wrap">
          <div
            className={`w-full ${months.length > 0 ? "md:w-1/2" : ""} pr-2 sm:pr-4`}
          >
            <h3 className={` ${"text-black"} leading-snug mb-3`}>
              {getDynamicReplacement("captain__targets__quarter_graph__title")}
            </h3>
            {fuelSaved > 0 ? (
              <div className="pb-4">
                {getDynamicReplacement(
                  "captain__targets__quarter_graph__fuel_saved",
                  {
                    COLOUR: bvrColor.name,
                    FUEL_SAVED: formatMass(industry, fuelSaved),
                  },
                )}
              </div>
            ) : (
              <div className="pb-4">{`You've not saved any fuel yet with ${target.behaviourAcronym}. Check back here in the future to see progress of your savings.`}</div>
            )}
            {!goalNeverAchieved &&
              bestFuelSaved > 0 &&
              Object.keys(tByGroup).length > 3 && (
                <div className="">
                  {getDynamicReplacement(
                    "captain__targets__quarter_graph__best_period",
                    {
                      COLOUR: bvrColor.name,
                      PERIOD: bestPeriod,
                    },
                  )}
                </div>
              )}
          </div>
          {months.length > 0 && (
            <div className="w-full md:w-1/2 p-2">
              <div className="mx-auto h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={500}
                    data={data.flatData}
                    margin={{
                      top: 25,
                      right: 15,
                      left: 15,
                      bottom: 0,
                    }}
                  >
                    <XAxis
                      dataKey="name"
                      type="category"
                      labels={false}
                      height={40}
                      stroke="#B4B4B7"
                    />
                    <YAxis
                      unit={`${data.units}`}
                      dataKey="y"
                      type="number"
                      stroke="#B4B4B7"
                      interval={0}
                      tickCount={maxY == 0 ? 2 : 10}
                      allowDecimals={false}
                      domain={([_dataMin, dataMax]) => {
                        const lowestMaximum = data.units === "kg" ? 100 : 0.2;
                        return [0, Math.max(lowestMaximum, dataMax * 1.2)];
                      }}
                      scale="linear"
                    >
                      <Label
                        value={`Fuel saved (${data.units}) with ${behaviour.behaviourAcronym}`}
                        offset={-10}
                        angle={-90}
                        position="insideLeft"
                        style={{
                          textAnchor: "middle",
                          fill: "#B4B4B7",
                          fontSize: "11",
                        }}
                      />
                    </YAxis>
                    <Bar dataKey="y" fill={bvrColor.codes[400]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>
    </CardContainer>
  );
};

export const GoalHistoricalPerformanceCardControls = {
  periods: {
    propType: PropTypes.array.isRequired,
    description: "Array of operator data",
    controlType: "object",
    defaultValue: [{ year: "2025", month: 1, value: { value: 10 } }],
  },
  baseline: {
    propType: PropTypes.object.isRequired,
    description: "Color mapping for behaviours",
    controlType: "object",
    defaultValue: { value: 25 },
  },
  bvrAcronym: {
    propType: PropTypes.string,
    description: "Behaviour acronym",
    controlType: "text",
  },
  dynamicContentFunc: {
    propType: PropTypes.func,
    description: "Function to get dynamic content",
    disable: true,
  },
  preferredEventName: {
    propType: PropTypes.string,
    description: "Preferred singular event name",
    controlType: "text",
  },
  preferredEventNamePlural: {
    propType: PropTypes.string,
    description: "Preferred plural event name",
    controlType: "text",
  },
  industry: COMMON_CONTROLS.industry,
  successChart: {
    propType: PropTypes.bool,
    description: "is success chart or not",
    controlType: "boolean",
  },
};

GoalHistoricalPerformanceCard.propTypes = AppUtils.getPropTypeFromControls(
  GoalHistoricalPerformanceCardControls,
);

export default GoalHistoricalPerformanceCard;
